import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { driver, type DriveStep, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { completeWelcomeNotification } from '@/composables/useDashboardWelcomeWizard'
import {
  getNavTourStepIds,
  getNavTourStepIcon,
  isNavTourFirstStep,
  isNavTourMenuStep,
  isNavTourMobileViewport,
  navTourSelector,
  appendPaidPlanStarToPopoverTitle,
  prependNavIconToPopoverTitle,
  appendTourNavigationPausedHint,
  pinMobileTourPopover,
  waitForNavTourSidebar,
  type NavTourStepId,
} from '@/helpers/dashboardNavTour'
import {
  closeDashboardMobileNav,
  openDashboardMobileNav,
} from '@/composables/useDashboardMobileNav'
import { useDonorPlan } from '@/composables/useDonorPlan'
import { useClaimTaxBack } from '@/composables/useClaimTaxBack'

let activeDriver: Driver | null = null
let tourOpenedMobileNav = false
/** Set when the donor finishes the last tour step; cleared on early exit. */
let tourCompletedSuccessfully = false

/**
 * Dashboard navigation tour via Driver.js.
 * On mobile, the first step highlights the menu button; Next opens the sidebar
 * before continuing through each nav item.
 */
export function useDashboardNavTour() {
  const { t } = useI18n()
  const { isPaid } = useDonorPlan()
  const { isClaimAvailable } = useClaimTaxBack()

  const tourStepIds = computed(() =>
    getNavTourStepIds(isPaid.value, {
      includeMobileMenu: isNavTourMobileViewport(),
      includeClaimTaxBack: isClaimAvailable.value,
    })
  )

  const closeMobileNavIfTourOpenedIt = () => {
    if (!tourOpenedMobileNav) {
      return
    }
    closeDashboardMobileNav()
    tourOpenedMobileNav = false
  }

  const finishTour = (markWelcomeComplete: boolean) => {
    closeMobileNavIfTourOpenedIt()
    if (markWelcomeComplete) {
      completeWelcomeNotification()
    }
  }

  const buildStepDescription = (stepId: NavTourStepId): string => {
    if (stepId === 'donations') {
      if (isPaid.value) {
        return t('Dashboard.Welcome.Tour.Steps.donations.PaidBody')
      }
      return t('Dashboard.Welcome.Tour.Steps.donations.Body')
    }
    if (stepId === 'statements') {
      if (isPaid.value) {
        return t('Dashboard.Welcome.Tour.Steps.statements.PaidBody')
      }
      return t('Dashboard.Welcome.Tour.Steps.statements.Body')
    }
    if (stepId === 'claim-tax-back') {
      if (isPaid.value) {
        return t('Dashboard.Welcome.Tour.Steps.claim-tax-back.PaidBody')
      }
      return t('Dashboard.Welcome.Tour.Steps.claim-tax-back.Body')
    }
    if (stepId === 'upgrade') {
      return t('Dashboard.Welcome.Tour.Steps.upgrade.Body')
    }
    return t(`Dashboard.Welcome.Tour.Steps.${stepId}.Body`)
  }

  const buildStepTitle = (stepId: NavTourStepId): string => {
    if (stepId === 'upgrade') {
      return t('Dashboard.Welcome.Tour.Steps.upgrade.Title')
    }
    return t(`Dashboard.Welcome.Tour.Steps.${stepId}.Title`)
  }

  const decoratePopoverForStep = (
    stepId: NavTourStepId,
    isMobile: boolean,
    popover: { title: HTMLElement; description: HTMLElement }
  ) => {
    const stepIcon = getNavTourStepIcon(stepId)
    if (stepIcon) {
      prependNavIconToPopoverTitle(popover.title, stepIcon)
    }
    if (stepId === 'upgrade') {
      appendPaidPlanStarToPopoverTitle(popover.title)
    }
    if (isNavTourFirstStep(stepId, isMobile)) {
      appendTourNavigationPausedHint(
        popover.description,
        t(
          isMobile
            ? 'Dashboard.Welcome.Tour.NavigationPausedHintMobile'
            : 'Dashboard.Welcome.Tour.NavigationPausedHint'
        )
      )
    }
  }

  const syncMobileNavForStep = async (
    stepId: NavTourStepId,
    driverInstance: Driver
  ) => {
    if (isNavTourMenuStep(stepId)) {
      closeDashboardMobileNav()
      tourOpenedMobileNav = false
    } else {
      openDashboardMobileNav()
      tourOpenedMobileNav = true
    }

    await waitForNavTourSidebar()
    driverInstance.refresh()
  }

  const startDriverTour = (isMobile: boolean) => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const stepIds = getNavTourStepIds(isPaid.value, {
      includeMobileMenu: isMobile,
      includeClaimTaxBack: isClaimAvailable.value,
    })
    tourCompletedSuccessfully = false

    const steps: DriveStep[] = stepIds.map((stepId) => ({
      element: navTourSelector(stepId),
      data: { id: stepId },
      onHighlightStarted: () => {
        if (!isMobile) {
          return
        }
        if (isNavTourMenuStep(stepId)) {
          closeDashboardMobileNav()
          tourOpenedMobileNav = false
          return
        }
        openDashboardMobileNav()
        tourOpenedMobileNav = true
      },
      onHighlighted: (element) => {
        if (isMobile && element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'auto' })
        }
      },
      popover: {
        title: buildStepTitle(stepId),
        description: buildStepDescription(stepId),
        side: isMobile ? 'bottom' : 'right',
        align: 'start',
        popoverClass: isMobile
          ? 'sa-driver-popover sa-driver-popover--mobile'
          : 'sa-driver-popover',
        onPopoverRender: (popover) => {
          decoratePopoverForStep(stepId, isMobile, popover)
          if (isMobile) {
            pinMobileTourPopover(popover)
            window.requestAnimationFrame(() => {
              activeDriver?.refresh()
            })
          }
        },
      },
    }))

    activeDriver = driver({
      // Snap highlights on mobile; animated stage morph fights the sliding sidebar.
      animate: !isMobile && !prefersReducedMotion,
      showProgress: true,
      progressText: '{{current}} of {{total}}',
      nextBtnText: t('Dashboard.Welcome.Tour.Next'),
      prevBtnText: t('Dashboard.Welcome.Tour.Back'),
      doneBtnText: t('Dashboard.Welcome.Tour.Finish'),
      popoverClass: 'sa-driver-popover',
      overlayColor: '#002d43',
      overlayOpacity: 0.45,
      stagePadding: 8,
      stageRadius: 12,
      allowClose: true,
      allowScroll: true,
      disableActiveInteraction: true,
      steps,
      onNextClick: (_element, _step, { driver: d, state }) => {
        const activeIndex = state.activeIndex
        if (activeIndex === undefined) {
          d.moveNext()
          return
        }

        const currentStepId = stepIds[activeIndex]
        const isLeavingMenuStep = isMobile && isNavTourMenuStep(currentStepId)

        void (async () => {
          if (isLeavingMenuStep) {
            await syncMobileNavForStep(stepIds[activeIndex + 1], d)
          }

          if (activeIndex === stepIds.length - 1) {
            tourCompletedSuccessfully = true
          }
          d.moveNext()
        })()
      },
      onPrevClick: (_element, _step, { driver: d, state }) => {
        const activeIndex = state.activeIndex
        if (activeIndex === undefined) {
          d.movePrevious()
          return
        }

        const previousStepId = stepIds[activeIndex - 1]
        const isReturningToMenuStep =
          isMobile && isNavTourMenuStep(previousStepId)

        void (async () => {
          if (isReturningToMenuStep) {
            await syncMobileNavForStep(previousStepId, d)
          }
          d.movePrevious()
        })()
      },
      onCloseClick: (_element, _step, { driver: d }) => {
        tourCompletedSuccessfully = false
        d.destroy()
      },
      onDestroyed: () => {
        activeDriver = null
        finishTour(tourCompletedSuccessfully)
        tourCompletedSuccessfully = false
      },
    })

    activeDriver.drive()
    if (isMobile) {
      window.requestAnimationFrame(() => {
        activeDriver?.refresh()
      })
    }
  }

  const startNavTour = () => {
    const isMobile = isNavTourMobileViewport()
    startDriverTour(isMobile)
  }

  const skipTour = () => {
    tourCompletedSuccessfully = false
    if (activeDriver?.isActive()) {
      activeDriver.destroy()
    }
  }

  return {
    tourStepIds,
    isPaid,
    startNavTour,
    skipTour,
    buildStepDescription,
  }
}
